import { useEffect, useRef, useState, type MouseEvent } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import { Button, Heading, Icon, Paragraph, Popover, TextLink } from '@/shared/components'
import { intlTag, useLocale, useT } from '@/shared/i18n'
import { jsonFetch } from '@/shared/lib/jsonFetch'
import { relativeTime } from '@/shared/lib/relativeTime'
import { NotificationContext, type NotificationContextProps } from './NotificationContext'

interface Notification extends NotificationContextProps {
  uuid: string
  title: string
  message: string
  subject_path: string | null
  read_at: string | null
  delivered_at: string | null
  created_at: string
}

export function NotificationBell() {
  const t = useT()
  const locale = useLocale()
  const { unread_count: sharedCount = 0 } = usePage().props as { unread_count?: number }
  const [count, setCount] = useState(sharedCount)
  const [open, setOpen] = useState(false)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(false)
  const [activating, setActivating] = useState<string | null>(null)
  const generation = useRef(0)
  const openRef = useRef(false)
  const savingRef = useRef(false)
  const activation = useRef<{ uuid: string } | null>(null)

  function changeOpen(next: boolean) {
    openRef.current = next
    if (!next) {
      generation.current += 1
      activation.current = null
      savingRef.current = false
      setSaving(false)
      setActivating(null)
    }
    setOpen(next)
  }

  useEffect(() => { setCount(sharedCount) }, [sharedCount])

  useEffect(() => {
    if (!open) return
    const owner = ++generation.current
    savingRef.current = false
    activation.current = null
    setSaving(false)
    setActivating(null)
    setLoading(true)
    setError(false)
    setNotifications([])
    void jsonFetch<{ notifications: Notification[]; unread_count: number }>('/notifications?preview=true').then((result) => {
      if (owner !== generation.current) return
      if (result.ok && result.body) {
        setNotifications(result.body.notifications)
        setCount(result.body.unread_count)
      } else setError(true)
      setLoading(false)
    })
    return () => {
      generation.current += 1
    }
  }, [open, locale])

  async function markAllRead() {
    if (!openRef.current || savingRef.current || activation.current || loading || count === 0) return
    const owner = generation.current
    savingRef.current = true
    setSaving(true)
    setError(false)
    const result = await jsonFetch<{ unread_count: number; read_at: string }>('/notifications/read-all', { method: 'PATCH' })
    if (owner !== generation.current) return
    if (result.ok && result.body) {
      const { unread_count, read_at } = result.body
      setCount(unread_count)
      setNotifications((items) => items.map((item) => ({ ...item, read_at: item.read_at ?? read_at })))
      router.reload({
        only: ['unread_count', 'notifications'],
        onError: () => { if (owner === generation.current) setError(true) },
        onFinish: () => {
          if (owner !== generation.current) return
          savingRef.current = false
          setSaving(false)
        },
      })
    } else {
      setError(true)
      savingRef.current = false
      setSaving(false)
    }
  }

  async function activate(event: MouseEvent<HTMLAnchorElement>, notification: Notification) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    event.preventDefault()
    if (!openRef.current || savingRef.current || activation.current?.uuid === notification.uuid) return
    const owner = generation.current
    const request = { uuid: notification.uuid }
    activation.current = request
    setActivating(notification.uuid)
    if (!notification.read_at) {
      // A read-status failure must not prevent the citizen reaching their case.
      await jsonFetch(`/notifications/${notification.uuid}/read`, { method: 'PATCH' })
    }
    if (owner !== generation.current || activation.current !== request) return
    changeOpen(false)
    router.visit(notification.subject_path ?? '/notifications')
  }

  return (
    <Popover open={open} onOpenChange={changeOpen} placement="bottom-end" ariaLabel={t('portal.notifications.title')}
      trigger={(
        <Button variant="ghost" style={{ position: 'relative' }} data-testid="bell"
          aria-label={t('portal.nav.bell_label', { count })}>
          <Icon name="bell" size={18} aria-hidden="true" />
          {count > 0 && <span className="topbar__bell-count" aria-hidden="true">{count}</span>}
        </Button>
      )}>
      <div className="notification-menu">
        <div className="popover__hd">
          <Heading level={2} data-size="xs" className="popover__title">{t('portal.notifications.title')}</Heading>
          <Button variant="ghost" size="sm" disabled={loading || saving || activating !== null || count === 0} onClick={() => void markAllRead()}>
            {t('portal.notifications.mark_all_read')}
          </Button>
        </div>
        <div className="notification-menu__body" aria-busy={loading || saving || activating !== null}>
          {loading && <Paragraph className="notification-menu__status" role="status">{t('portal.notifications.loading')}</Paragraph>}
          {error && <Paragraph className="notification-menu__status" role="alert">{t('portal.notifications.menu_error')}</Paragraph>}
          {!loading && !error && notifications.length === 0 && (
            <Paragraph className="notification-menu__status">{t('portal.notifications.empty')}</Paragraph>
          )}
          {!loading && notifications.length > 0 && (
            <ul className="notification-menu__list" aria-label={t('portal.notifications.title')}>
              {notifications.map((notification) => {
                const unread = !notification.read_at
                const timestamp = notification.delivered_at ?? notification.created_at
                return (
                  <li key={notification.uuid}>
                    <TextLink href={notification.subject_path ?? '/notifications'}
                      className={`notif notification-menu__item${unread ? ' notif--unread' : ''}`}
                      aria-busy={activating === notification.uuid}
                      onClick={(event) => void activate(event, notification)}>
                      <div className="notification-menu__heading">
                        <Heading level={3} data-size="xs" className="notif__title">{notification.title}</Heading>
                        <time className="notif__time" dateTime={timestamp} title={new Date(timestamp).toLocaleString(intlTag(locale))}>
                          {relativeTime(timestamp, locale)}
                        </time>
                      </div>
                      <Paragraph asChild><span className="notif__sub">{notification.message}</span></Paragraph>
                      <NotificationContext case_label={notification.case_label} due_on={notification.due_on} />
                      {unread && <span className="sr-only">{t('portal.notifications.unread')}</span>}
                    </TextLink>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
        <div className="popover__ft">
          <TextLink asChild><Link href="/notifications" onClick={event => {
            if (event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) changeOpen(false)
          }}>{t('portal.dashboard.tasks.notifications')}</Link></TextLink>
        </div>
      </div>
    </Popover>
  )
}
